/**
 * SidebarAccountZone — the clean-slate (v2) chrome's account zone: Upgrade ·
 * Units · the account block. Lives at the foot of the Sidebar, gated on
 * `isChromeV2()`.
 *
 * This is the v2 twin of shared/GlobalControls (v1's header/row controls) —
 * NOT a replacement. The two menus must stay in sync until v1 retires with
 * the flag flip: the same account items (photo, password, role,
 * notifications, dark-mode toggle for super admins, sign out) and the same
 * admin-only "view as user" affordance live in both, just on different
 * ground and in a different arrangement. The super-admin founder tools
 * (Support Console · Command Center) render in both via the one shared
 * SuperAdminMenuItems component (Brian, 2026-07-19).
 *
 * Design: docs/design/chrome-cleanslate-hifi.html, States A + C (approved by
 * Brian 2026-07-18). State C's revision is why Units renders ALWAYS VISIBLE
 * here, in the open, rather than folded inside the account menu — Brian's
 * verbatim call: "the only thing i am not sold on is the location of the
 * units hours/points. i think it should not be buried in the profile
 * settings. it needs to be more visible." The Admin/User-view switch stays
 * inside the menu (he raised no objection there).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/useAuthStore';
import { useThemeStore } from '../store/useThemeStore';
import { usePlan } from '../lib/usePlan';
import { orgApi } from '../lib/api';
import { resetAnalytics } from '../lib/analytics';
import Avatar from './shared/Avatar';
import SuperAdminMenuItems from './shared/SuperAdminMenuItems';

// Dark-mode toggle menu item (N1 dev preview — super admins only, mirrors
// GlobalControls' ThemeMenuItem). Deliberately does NOT close the menu, so
// flipping back is one click.
function ThemeMenuItem() {
  const { theme, toggleTheme } = useThemeStore();
  const isSuperAdmin = useAuthStore(s => s.isSuperAdmin);
  if (!isSuperAdmin()) return null;
  return (
    <button
      onClick={toggleTheme}
      className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
    >
      {theme === 'dark' ? 'Light mode' : 'Dark mode'}
    </button>
  );
}

export default function SidebarAccountZone() {
  const { user, logout, viewAsUser, toggleViewAsUser } = useAuthStore();
  const isActualAdmin = user?.role === 'admin';
  const { plan, metricUnit } = usePlan();
  const showUpgrade = isActualAdmin && !viewAsUser && plan?.plan === 'free';
  // The capacity unit (Hours/Points) is org-wide; only an admin (not in user-
  // preview) can flip it. Everyone else sees the active unit read-only —
  // same rule as GlobalControls.
  const canSetUnit = isActualAdmin && !viewAsUser;
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [savingUnit, setSavingUnit] = useState(false);

  const goToAccount = (hash: 'photo' | 'password' | 'role') => {
    setShowMenu(false);
    navigate(`/settings/account#${hash}`);
  };

  async function setUnit(u: 'hours' | 'points') {
    if (u === metricUnit || savingUnit) return;
    setSavingUnit(true);
    try {
      await orgApi.setMetricUnit(u);
      // Org-wide, read server-side by nearly every data surface — reload so
      // the whole app reflects the switch at once (mirrors GlobalControls).
      window.location.reload();
    } catch {
      toast.error('Could not change units');
      setSavingUnit(false);
    }
  }

  const handleLogout = () => {
    resetAnalytics();
    logout();
    navigate('/login');
  };

  const roleLabel = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : '';

  return (
    <div>
      {showUpgrade && (
        <button
          onClick={() => navigate('/admin/billing')}
          title="Upgrade to Team or Business for more seats, Jira sync, and Avium Signals"
          className="mx-3 mb-2 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-spark text-white hover:bg-brand-spark-dark transition-colors"
        >
          <span>⚡</span> Upgrade Plan
        </button>
      )}

      {/* Units — always visible, never buried in the menu (Brian, 2026-07-18). */}
      <div
        className="mx-3 mb-2 px-2.5 py-1.5 rounded-lg bg-white/[0.07] flex items-center justify-between gap-2"
        title="Capacity unit, org-wide — Hours (tracked time) or Story Points (committed)"
      >
        <span className="text-teal-300 text-[10px] font-bold tracking-wide">UNITS</span>
        {canSetUnit ? (
          <div className="flex items-center rounded-full border border-teal-400 overflow-hidden text-xs font-semibold">
            {(['hours', 'points'] as const).map(u => (
              <button
                key={u}
                onClick={() => setUnit(u)}
                disabled={savingUnit}
                className={`px-2.5 py-1 transition-colors disabled:opacity-60 ${
                  metricUnit === u ? 'bg-card text-brand-primary' : 'text-teal-100 hover:bg-teal-800/40'
                }`}
              >
                {u === 'hours' ? 'Hours' : 'Points'}
              </button>
            ))}
          </div>
        ) : (
          <span className="text-teal-100 border border-teal-700 text-xs font-semibold px-2 py-0.5 rounded-full">
            {metricUnit === 'points' ? 'Points' : 'Hours'}
          </span>
        )}
      </div>

      {/* Account block — the sidebar's foot. */}
      <div className="relative border-t border-teal-800">
        <button
          onClick={() => setShowMenu(v => !v)}
          className="flex items-center gap-2.5 px-4 py-3 text-left hover:bg-teal-800/40 transition-colors w-full"
        >
          <Avatar name={user?.name} avatarUrl={user?.avatarUrl} size={28} className="ring-1 ring-white/25" />
          <div className="min-w-0">
            <div className="text-white text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-teal-400 text-[10px]">
              {roleLabel}{viewAsUser ? ' · User view' : ''}
            </div>
          </div>
          <span className="ml-auto text-teal-300 text-xs">▾</span>
        </button>

        {showMenu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
            <div className="absolute bottom-full left-2 right-2 mb-1 bg-card rounded-lg shadow-lg border border-line py-1 z-50">
              {user?.name && (
                <div className="px-4 py-2 border-b border-line">
                  <div className="text-sm font-semibold text-ink truncate">{user.name}</div>
                </div>
              )}
              {isActualAdmin && (
                <button
                  onClick={toggleViewAsUser}
                  className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
                >
                  {viewAsUser ? '👤 User View — click to return to Admin' : '🛡️ Preview as user'}
                </button>
              )}
              <button
                onClick={() => goToAccount('photo')}
                className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
              >
                Change profile picture
              </button>
              <button
                onClick={() => goToAccount('password')}
                className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
              >
                Change Password
              </button>
              <button
                onClick={() => goToAccount('role')}
                className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
              >
                Your role
              </button>
              <button
                onClick={() => { setShowMenu(false); navigate('/settings/notifications'); }}
                className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
              >
                Notifications
              </button>
              <ThemeMenuItem />
              <SuperAdminMenuItems onClose={() => setShowMenu(false)} />
              <hr className="my-1" />
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
